<template>
  <transition name="cc-fade">
    <div
      v-if="visible"
      class="cookie-consent no-print"
      role="dialog"
      aria-live="polite"
      :aria-label="t('cookieConsent.title')"
    >
      <div class="cookie-consent__card">
        <div class="cookie-consent__text">
          <h2 class="cookie-consent__title">{{ t('cookieConsent.title') }}</h2>
          <p class="cookie-consent__message">{{ t('cookieConsent.message') }}</p>
          <RouterLink :to="localePath('/data-and-privacy')" class="cookie-consent__learn-more">
            {{ t('cookieConsent.learnMore') }}
          </RouterLink>
        </div>
        <div class="cookie-consent__actions">
          <button
            type="button"
            class="cookie-consent__btn cookie-consent__btn--decline"
            @click="decline"
          >
            {{ t('cookieConsent.decline') }}
          </button>
          <button
            type="button"
            class="cookie-consent__btn cookie-consent__btn--accept"
            @click="accept"
          >
            {{ t('cookieConsent.accept') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * CookieConsent.vue — GDPR opt-in banner (spec 049-google-analytics, Task 2.2).
 *
 * Shows once until the user accepts or declines; the decision is persisted in
 * localStorage (`cpt_cookie_consent`). On accept it emits `accept` so the root
 * App component can initialize GA4 (NFR-001 — nothing tracks before consent).
 */
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';
import { localePath } from '@/router/localePath';
import { getConsent, setConsent } from '@/utils/analytics';

const { t } = useI18n();
const emit = defineEmits<{ (e: 'accept'): void; (e: 'decline'): void }>();

const visible = ref(false);

const handleReopen = () => { visible.value = true; };

onMounted(() => {
  visible.value = getConsent() === null;
  window.addEventListener('cpt:show-cookie-consent', handleReopen);
});

onUnmounted(() => {
  window.removeEventListener('cpt:show-cookie-consent', handleReopen);
});

function accept(): void {
  setConsent('accepted');
  visible.value = false;
  emit('accept');
}

function decline(): void {
  setConsent('declined');
  visible.value = false;
  emit('decline');
}
</script>

<style scoped>
.cookie-consent {
  position: fixed;
  left: 50%;
  bottom: 1.25rem;
  transform: translateX(-50%);
  width: min(40rem, calc(100vw - 2rem));
  z-index: 60;
}

.cookie-consent__card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  background: rgb(15 23 42 / 0.72);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgb(255 255 255 / 0.15);
  box-shadow: 0 12px 40px rgb(0 0 0 / 0.35);
  color: #f8fafc;
}

.cookie-consent__text {
  flex: 1 1 18rem;
  min-width: 0;
}

.cookie-consent__title {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  font-weight: 700;
}

.cookie-consent__message {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgb(226 232 240 / 0.9);
}

.cookie-consent__learn-more {
  display: inline-block;
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #67e8f9;
  text-decoration: underline;
}

.cookie-consent__learn-more:hover {
  opacity: 0.85;
}

.cookie-consent__actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.cookie-consent__btn {
  padding: 0.5rem 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.cookie-consent__btn--decline {
  background: rgb(255 255 255 / 0.08);
  border: 1px solid rgb(255 255 255 / 0.25);
  color: #e2e8f0;
}

.cookie-consent__btn--decline:hover {
  background: rgb(255 255 255 / 0.16);
}

.cookie-consent__btn--accept {
  background: linear-gradient(135deg, #06b6d4, #6366f1);
  border: 1px solid transparent;
  color: #fff;
}

.cookie-consent__btn--accept:hover {
  opacity: 0.92;
}

.cc-fade-enter-active,
.cc-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.cc-fade-enter-from,
.cc-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 0.75rem);
}
</style>
